import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Plus, Trash2, Pencil, X, Target, TrendingUp, CheckCircle2, AlertTriangle,
  Search, Upload, Download, LayoutTemplate, Wand2, Save, Copy,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCabangOptions } from "../lib/cabang";
import { MonthPicker } from "@/components/ui/date-picker";
import { bulanIniISO } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

const blankKpi = () => ({
  nama_kpi: "", target_value: 0, unit: "unit", realisasi_value: 0, catatan: "", sumber: "manual",
});

const emptyForm = () => ({
  emp_id: "",
  emp_ids: [],
  periode: bulanIniISO(),
  kpis: [blankKpi()],
});

export default function KpiTargets() {
  const { can } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  // Kunci modul `sales_targets` — sama dengan gerbang menu di Layout.jsx DAN gerbang
  // endpoint di routers/kpi_targets.py. Sebelumnya halaman ini mengunci semua tombol
  // ke role admin/superadmin, jadi preset manager_cabang (sales_targets.update = True)
  // membuka halaman yang isinya cuma bisa dibaca. Dipisah per aksi supaya yang tampil
  // persis yang backend loloskan — bukan satu saklar untuk tiga aksi berbeda.
  const canCreate = can("sales_targets", "create");
  const canUpdate = can("sales_targets", "update");
  const canDelete = can("sales_targets", "delete");
  // Kelola template = create/update/delete template; buka pintunya kalau salah satu ada.
  const canKelolaTemplate = canCreate || canUpdate || canDelete;
  const [periode, setPeriode] = useState(bulanIniISO());
  const [branchFilter, setBranchFilter] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [emps, setEmps] = useState([]);
  const [units, setUnits] = useState(["unit", "Rp", "kali", "poin", "%", "hari", "jam", "lembar", "kunjungan"]);
  const [templates, setTemplates] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [saveResult, setSaveResult] = useState(null); // hasil bulk terakhir: {dibuat,dilewati,gagal,rincian}
  const [empSearch, setEmpSearch] = useState("");
  const [empDivisiFilter, setEmpDivisiFilter] = useState("");
  const [empJabatanFilter, setEmpJabatanFilter] = useState("");
  // Template mgmt modal
  const [tplModal, setTplModal] = useState(null); // null | { mode:"list"|"edit", tpl }

  // Setiap pemuatan awal WAJIB punya .catch. Tanpa itu kegagalannya jadi unhandled
  // rejection tanpa jejak di layar: daftar template diam-diam kosong dan halaman
  // terlihat seperti "memang belum ada template", bukan seperti gagal memuat.
  // `lingkup=all` WAJIB di halaman ini. Endpoint /kpi-custom dipakai bersama
  // "KPI Saya" (MyKpi.jsx) yang memang harus terkunci ke diri sendiri, jadi
  // bawaannya menyaring per employee_id. Tanpa param ini preset manager_cabang
  // (sales_targets: view+update, tanpa baris my_kpi) mendapat halaman kelola
  // yang selalu melaporkan 0 KPI padahal tombol simpannya berhasil menulis.
  // Backend menurunkan permintaan ini jadi "own" kalau memang tidak berhak dan
  // mengabarkannya lewat data.lingkup — dibaca di empty-state di bawah.
  const load = useCallback(() => api.get(`/kpi-custom?periode=${periode}&lingkup=all`).then(r => setData(r.data))
    .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat KPI")), [periode]);
  const loadTemplates = useCallback(() => api.get("/kpi-templates").then(r => setTemplates(r.data || []))
    .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat template KPI")), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/employees").then(r => setEmps((r.data || []).filter(e => e.is_active !== false)))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat daftar karyawan"));
    api.get("/kpi-custom/units").then(r => setUnits(current => r.data.units || current)).catch(() => {});
    loadTemplates();
  }, [loadTemplates]);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/kpi-custom/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "template_kpi_custom.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Template diunduh");
    } catch { toast.error("Gagal unduh template"); }
  };
  const handleImport = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      toast.info(`Mengupload ${file.name}…`);
      const resp = await api.post("/kpi-custom/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(resp.data);
      const { created, updated, skipped } = resp.data;
      toast.success(`Import selesai: ${created} baru · ${updated} update · ${skipped} skip`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal import Excel"); }
  };

  const resetEmpPicker = () => { setEmpSearch(""); setEmpDivisiFilter(""); setEmpJabatanFilter(""); };
  const openNew = () => {
    setForm({ ...emptyForm(), periode }); setEditingId(null); setSaveResult(null); resetEmpPicker(); setModal(true);
  };
  const openEdit = (kpi) => {
    setForm({
      emp_id: kpi.emp_id, emp_ids: [], periode: kpi.periode,
      kpis: [{
        nama_kpi: kpi.nama_kpi, target_value: kpi.target_value, unit: kpi.unit,
        realisasi_value: kpi.realisasi_value, catatan: kpi.catatan || "",
        sumber: kpi.sumber || "manual",
      }],
    });
    setEditingId(kpi.id);
    setSaveResult(null);
    setModal(true);
  };
  const closeModal = () => { setModal(false); setSaveResult(null); };

  const addKpiRow = () => setForm(s => ({ ...s, kpis: [...s.kpis, blankKpi()] }));
  const removeKpiRow = (idx) => setForm(s => ({ ...s, kpis: s.kpis.length > 1 ? s.kpis.filter((_, i) => i !== idx) : s.kpis }));
  const updKpiRow = (idx, patch) => setForm(s => ({ ...s, kpis: s.kpis.map((k, i) => i === idx ? { ...k, ...patch } : k) }));

  // Pemilih multi-karyawan
  const divisiOptions = useMemo(() => [...new Set(emps.map(e => e.divisi).filter(Boolean))].sort(), [emps]);
  const jabatanOptions = useMemo(() => [...new Set(emps.map(e => e.grade_jabatan).filter(Boolean))].sort(), [emps]);
  const modalFilteredEmps = useMemo(() => emps.filter(e => {
    if (empSearch && !(e.nama || "").toLowerCase().includes(empSearch.toLowerCase())) return false;
    if (empDivisiFilter && e.divisi !== empDivisiFilter) return false;
    if (empJabatanFilter && e.grade_jabatan !== empJabatanFilter) return false;
    return true;
  }), [emps, empSearch, empDivisiFilter, empJabatanFilter]);
  const toggleEmpSelect = (id) => setForm(s => ({
    ...s, emp_ids: s.emp_ids.includes(id) ? s.emp_ids.filter(x => x !== id) : [...s.emp_ids, id],
  }));
  const selectAllVisibleEmp = () => setForm(s => ({
    ...s, emp_ids: [...new Set([...s.emp_ids, ...modalFilteredEmps.map(e => e.id)])],
  }));
  const clearEmpSelection = () => setForm(s => ({ ...s, emp_ids: [] }));
  const validKpiCount = useMemo(() => form.kpis.filter(k => k.nama_kpi.trim()).length, [form.kpis]);

  // Load template into form
  const applyTemplate = (tplId, replace = true) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    const items = (tpl.items || []).map(it => ({
      nama_kpi: it.nama_kpi,
      target_value: it.target_value || 0,
      unit: it.unit || "unit",
      realisasi_value: 0,
      catatan: it.catatan || "",
      sumber: "manual",
    }));
    setForm(s => ({ ...s, kpis: replace ? items : [...s.kpis.filter(k => k.nama_kpi.trim()), ...items] }));
    toast.success(`Template "${tpl.nama_template}" diterapkan (${items.length} KPI)`);
  };

  // Sarankan template berdasarkan jabatan karyawan — hanya jika tepat 1 karyawan yang ditarget
  const primaryEmpId = editingId ? form.emp_id : (form.emp_ids.length === 1 ? form.emp_ids[0] : "");
  const suggestedTemplates = useMemo(() => {
    if (!primaryEmpId) return [];
    const emp = emps.find(e => e.id === primaryEmpId);
    const jabatan = (emp?.grade_jabatan || "").toLowerCase().trim();
    if (!jabatan) return [];
    return templates.filter(t => (t.grade_jabatan || "").toLowerCase().trim() === jabatan);
  }, [primaryEmpId, emps, templates]);

  const save = async () => {
    const validKpis = form.kpis.filter(k => k.nama_kpi.trim());
    if (validKpis.length === 0) { toast.error("Minimal 1 KPI dengan nama"); return; }
    try {
      if (editingId) {
        if (!form.emp_id) { toast.error("Pilih karyawan dulu"); return; }
        // Single edit path — first kpi in the form
        const payload = { ...validKpis[0] };
        if (payload.sumber === "csat") delete payload.realisasi_value; // backend menolak perubahan ini untuk sumber CSAT
        await api.patch(`/kpi-custom/${editingId}`, payload);
        toast.success("KPI diperbarui");
        setModal(false);
      } else {
        if (form.emp_ids.length === 0) { toast.error("Pilih minimal 1 karyawan"); return; }
        const { data: resp } = await api.post("/kpi-custom/bulk", {
          emp_ids: form.emp_ids, periode: form.periode, kpis: validKpis,
        });
        const dibuat = resp.dibuat ?? resp.created ?? 0;
        const dilewati = resp.dilewati ?? 0;
        const gagal = resp.gagal ?? 0;
        const parts = [`${dibuat} KPI dibuat`];
        if (dilewati > 0) parts.push(`${dilewati} dilewati (sudah ada)`);
        if (gagal > 0) parts.push(`${gagal} gagal`);
        const msg = parts.join(" · ");
        if (gagal > 0) toast.error(msg);
        else if (dilewati > 0) toast.warning(msg);
        else toast.success(msg);
        setSaveResult(resp);
        // Tutup modal otomatis hanya kalau semuanya sukses bersih; kalau ada dilewati/gagal, biarkan
        // terbuka supaya panel rincian di bawah tetap terlihat — jangan sembunyikan kegagalan.
        if (gagal === 0 && dilewati === 0) setModal(false);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const del = async (id) => {
    if (!confirm("Hapus KPI ini?")) return;
    try { await api.delete(`/kpi-custom/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };
  const quickUpdateRealisasi = async (id, value) => {
    try { await api.patch(`/kpi-custom/${id}`, { realisasi_value: parseFloat(value) || 0 }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Backend menjawab lingkup yang BENAR-BENAR dipakainya (bukan yang diminta):
  // "own" berarti permintaan lingkup=all di atas diturunkan karena izinnya belum
  // cukup. Dipakai supaya layar kosong menjelaskan sebabnya, bukan berbohong
  // "belum ada data". Respons lama tanpa field ini → undefined → dianggap tidak
  // terbatas, jadi kompatibel mundur.
  // "cabang" ikut dihitung terbatas sejak lingkup ketiga ada (21 Agu 2026).
  // Memeriksa `=== "own"` saja membuat pemegang lingkup cabang yang kebetulan
  // belum punya KPI melihat "Belum ada KPI kustom" — berbohong, karena yang
  // terjadi adalah penyaringan, bukan ketiadaan data.
  const lingkupCabang = data?.lingkup === "cabang";
  const lingkupTerbatas = data?.lingkup === "own" || lingkupCabang;

  const filteredEmp = useMemo(() => {
    if (!data) return [];
    const src = data.by_employee || [];
    return src.filter(row => {
      if (branchFilter && row.branch !== branchFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!(row.employee_nama || "").toLowerCase().includes(s)
            && !row.kpis.some(k => (k.nama_kpi || "").toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [data, branchFilter, q]);

  const formatValue = (v, unit) => {
    if (unit === "Rp") return "Rp " + new Intl.NumberFormat("id-ID").format(v || 0);
    return `${new Intl.NumberFormat("id-ID").format(v || 0)} ${unit || ""}`.trim();
  };

  // ---- Template CRUD helpers ----
  const openTplList = () => setTplModal({ mode: "list" });
  const openTplNew = () => setTplModal({
    mode: "edit",
    tpl: { id: null, nama_template: "", grade_jabatan: "", deskripsi: "", items: [{ nama_kpi: "", target_value: 0, unit: "unit", catatan: "" }] },
  });
  const openTplEdit = (tpl) => setTplModal({ mode: "edit", tpl: JSON.parse(JSON.stringify(tpl)) });
  const saveTpl = async () => {
    const tpl = tplModal?.tpl; if (!tpl) return;
    if (!tpl.nama_template.trim()) { toast.error("Nama template wajib"); return; }
    const items = (tpl.items || []).filter(i => (i.nama_kpi || "").trim());
    if (items.length === 0) { toast.error("Minimal 1 item KPI"); return; }
    try {
      if (tpl.id) {
        await api.patch(`/kpi-templates/${tpl.id}`, { ...tpl, items });
        toast.success("Template diperbarui");
      } else {
        await api.post("/kpi-templates", { ...tpl, items });
        toast.success("Template dibuat");
      }
      await loadTemplates();
      setTplModal({ mode: "list" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Kedua modal (multi-baris KPI & template) ada di JSX halaman yang tidak
  // unmount — `active` mengikuti state buka masing-masing supaya dirty-state
  // di-reset tiap kali dibuka lagi untuk karyawan/template lain.
  const kpiModalGuard = useModalGuard({ active: modal, dangerous: true, onClose: closeModal, onSubmit: save });
  const kpiTplGuard = useModalGuard({ active: !!tplModal, dangerous: true, onClose: () => setTplModal(null), onSubmit: saveTpl });

  const delTpl = async (id) => {
    if (!confirm("Hapus template ini?")) return;
    try { await api.delete(`/kpi-templates/${id}`); toast.success("Terhapus"); await loadTemplates(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]" data-testid="kpi-custom-page">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 mb-2">KPI Kustom</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">KPI Non-Sales · Custom Target</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Tetapkan <b>banyak KPI sekaligus</b> per karyawan. Gunakan <b>Template Jabatan</b> untuk auto-populate KPI standar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthPicker value={periode} onChange={setPeriode}
            data-testid="kpi-periode" />
          <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
            data-testid="kpi-branch"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[160px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari karyawan/KPI..."
              data-testid="kpi-search"
              className="h-10 pl-8 pr-3 rounded-lg border border-slate-200 text-sm w-56" />
          </div>
          {canKelolaTemplate && (
            <button onClick={openTplList} data-testid="btn-manage-kpi-tpl"
              title="Kelola template KPI per jabatan"
              className="h-10 px-3 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-violet-800 text-sm inline-flex items-center gap-1.5 font-medium">
              <LayoutTemplate className="w-4 h-4" /> Template Jabatan
              {templates.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold">{templates.length}</span>}
            </button>
          )}
          {/* Unduh template Excel & Import satu paket: backend memetakan keduanya ke
              `sales_targets.create` karena berkasnya cuma berguna untuk diimpor balik. */}
          {canCreate && (
            <>
              <button onClick={downloadTemplate} data-testid="btn-download-kpi-template"
                title="Download template Excel"
                className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm inline-flex items-center gap-1.5 text-slate-700">
                <Download className="w-4 h-4" /> Excel
              </button>
              <label data-testid="btn-import-kpi"
                title="Import KPI dari Excel"
                className="h-10 px-3 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm inline-flex items-center gap-1.5 cursor-pointer">
                <Upload className="w-4 h-4" /> Import
                <input type="file" accept=".xlsx,.xls" className="hidden"
                  onChange={e => e.target.files?.[0] && handleImport(e.target.files[0])} />
              </label>
              <button onClick={openNew} data-testid="btn-add-kpi"
                className="h-10 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Tambah KPI
              </button>
            </>
          )}
        </div>
      </header>

      {importResult && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="kpi-import-result">
          <div className="flex items-start justify-between mb-2">
            <p className="font-display font-semibold text-slate-900 text-sm">Hasil Import Excel</p>
            <button onClick={() => setImportResult(null)} className="p-1 hover:bg-white rounded"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Baru:</span> <b className="font-mono text-emerald-700">{importResult.created}</b></div>
            <div><span className="text-slate-500">Update:</span> <b className="font-mono text-sky-700">{importResult.updated}</b></div>
            <div><span className="text-slate-500">Skip:</span> <b className="font-mono text-rose-700">{importResult.skipped}</b></div>
            <div><span className="text-slate-500">Total diproses:</span> <b className="font-mono text-slate-900">{importResult.total_processed}</b></div>
          </div>
          {(importResult.errors || []).length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-rose-700 font-medium">Detail error ({importResult.errors.length})</summary>
              <ul className="mt-2 list-disc pl-5 space-y-0.5 text-slate-600 max-h-40 overflow-auto">
                {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <SumCard label="Total KPI" value={data.summary.total_kpi} tint="violet" icon={Target} />
          <SumCard label="Karyawan" value={data.summary.total_employees} tint="slate" icon={TrendingUp} />
          <SumCard label="On-Track (≥100%)" value={data.summary.on_track} tint="emerald" icon={CheckCircle2} />
          <SumCard label="Belum Tercapai" value={data.summary.total_kpi - data.summary.on_track} tint="amber" icon={AlertTriangle} />
        </div>
      )}

      {filteredEmp.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">
            {lingkupCabang
              ? "Kamu hanya melihat KPI cabangmu"
              : lingkupTerbatas
                ? "Kamu hanya melihat KPI milikmu sendiri"
                : "Belum ada KPI kustom untuk periode ini"}
          </p>
          {/* Layar kosong TANPA penjelasan adalah sebab keluhan "halamannya rusak".
              Kalau backend menurunkan lingkup jadi "own", katakan apa adanya berikut
              jalan keluarnya — jangan biarkan terbaca seperti "memang belum ada data". */}
          {lingkupTerbatas && (
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto" data-testid="kpi-lingkup-terbatas">
              Izinmu di modul <b>Target &amp; KPI</b> masih berlingkup{" "}
              {lingkupCabang ? "hanya cabangmu" : "data sendiri"}, jadi KPI karyawan{" "}
              {lingkupCabang ? "cabang lain" : "lain"} tidak ditarik ke halaman ini. Minta admin
              menyetel lingkup modul itu jadi &ldquo;semua&rdquo; di User Management.
            </p>
          )}
          {!lingkupTerbatas && !canCreate && (
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto" data-testid="kpi-tanpa-izin-create">
              Izinmu hanya mencakup mengubah KPI yang sudah ada, bukan membuat yang baru.
              Minta admin atau HR membuat KPI-nya lebih dulu.
            </p>
          )}
          {canCreate && (
            <button onClick={openNew} data-testid="btn-add-first-kpi"
              className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Buat KPI Pertama
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredEmp.map((row, i) => (
            <motion.div key={row.emp_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid={`kpi-emp-card-${row.emp_id}`}
              className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-start justify-between mb-4 border-b border-slate-100 pb-3">
                <div>
                  <p className="font-display font-semibold text-slate-900">{row.employee_nama}</p>
                  <p className="text-xs text-slate-500">{row.branch || "-"} · {row.kpis.length} KPI</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    row.kpis.every(k => k.pct >= 100) ? "bg-emerald-100 text-emerald-800" :
                    row.kpis.some(k => k.pct >= 100) ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
                  }`}>
                    {row.kpis.filter(k => k.pct >= 100).length} / {row.kpis.length}
                  </span>
                  {canCreate && (
                    <button onClick={() => {
                      setForm({ ...emptyForm(), emp_ids: [row.emp_id], periode });
                      setEditingId(null);
                      setSaveResult(null);
                      resetEmpPicker();
                      setModal(true);
                    }} data-testid={`btn-add-more-kpi-${row.emp_id}`}
                      title="Tambah KPI lain ke karyawan ini"
                      className="p-1.5 rounded hover:bg-violet-100 text-violet-700">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                {row.kpis.map(k => {
                  const isCsat = k.sumber === "csat";
                  const kosong = isCsat && k.realisasi_kosong;
                  return (
                  <div key={k.id} data-testid={`kpi-row-${k.id}`} className="border border-slate-100 rounded-lg p-3 hover:border-violet-200 transition-colors">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium text-slate-900 truncate">{k.nama_kpi}</p>
                          {isCsat && (
                            <span data-testid={`kpi-badge-csat-${k.id}`}
                              className="shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-sky-100 text-sky-800">
                              Otomatis dari CSAT
                            </span>
                          )}
                        </div>
                        {k.catatan && <p className="text-[11px] text-slate-500 italic truncate">{k.catatan}</p>}
                      </div>
                      {kosong ? (
                        <span data-testid={`kpi-belum-respons-${k.id}`}
                          className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500">
                          Belum ada respons
                        </span>
                      ) : (
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          k.pct >= 100 ? "bg-emerald-100 text-emerald-800" :
                          k.pct >= 75 ? "bg-sky-100 text-sky-800" :
                          k.pct >= 50 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                        }`}>{k.pct}%</span>
                      )}
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
                      <div className={`h-full ${kosong ? "bg-slate-300" :
                        k.pct >= 100 ? "bg-emerald-500" :
                        k.pct >= 75 ? "bg-sky-500" :
                        k.pct >= 50 ? "bg-amber-500" : "bg-rose-500"
                      }`} style={{ width: `${kosong ? 0 : Math.min(100, k.pct)}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        Realisasi: {kosong ? (
                          <b className="text-slate-400 italic font-normal">Belum ada respons</b>
                        ) : (
                          <b className="text-slate-800 font-mono">{formatValue(k.realisasi_value, k.unit)}</b>
                        )}
                        <span className="text-slate-400"> / {formatValue(k.target_value, k.unit)}</span>
                      </span>
                      {(canUpdate || canDelete) && (
                        <div className="flex items-center gap-1">
                          {canUpdate && !isCsat && (
                            <input type="number" defaultValue={k.realisasi_value}
                              onBlur={(e) => {
                                const v = parseFloat(e.target.value) || 0;
                                if (v !== k.realisasi_value) quickUpdateRealisasi(k.id, v);
                              }}
                              data-testid={`kpi-quick-realisasi-${k.id}`}
                              title="Ubah realisasi (blur untuk simpan)"
                              className="w-20 h-7 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                          )}
                          {canUpdate && (
                            <button onClick={() => openEdit(k)} data-testid={`btn-edit-kpi-${k.id}`}
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => del(k.id)} data-testid={`btn-del-kpi-${k.id}`}
                              className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit KPI Modal (Multi-row) */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={kpiModalGuard.requestClose}>
          <div ref={kpiModalGuard.contentRef} className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()} data-testid="kpi-modal">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">
                {editingId ? "Edit KPI" : "Tambah KPI Kustom (Multi-karyawan)"}
              </h3>
              <button onClick={kpiModalGuard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Periode</span>
                  <MonthPicker value={form.periode} onChange={v => setForm(s => ({ ...s, periode: v }))}
                    data-testid="kpi-periode-input" disabled={!!editingId}
                    className="mt-1" />
                </label>
                {editingId && (
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Karyawan</span>
                    <input type="text" disabled
                      value={emps.find(e => e.id === form.emp_id)?.nama || form.emp_id}
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-600" />
                  </label>
                )}
              </div>

              {/* Pemilih multi-karyawan (hanya saat menambah baru) */}
              {!editingId && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2">
                    <p className="text-xs font-semibold text-slate-700">Pilih Karyawan *</p>
                  </div>
                  <div className="p-3 space-y-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative flex-1 min-w-[180px]">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                          data-testid="kpi-emp-search" placeholder="Cari nama karyawan..."
                          className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-xs w-full" />
                      </div>
                      {divisiOptions.length > 0 && (
                        <select value={empDivisiFilter} onChange={e => setEmpDivisiFilter(e.target.value)}
                          data-testid="kpi-filter-divisi"
                          className="h-9 px-2 rounded-lg border border-slate-200 text-xs bg-white">
                          <option value="">Semua Divisi</option>
                          {divisiOptions.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      )}
                      {jabatanOptions.length > 0 && (
                        <select value={empJabatanFilter} onChange={e => setEmpJabatanFilter(e.target.value)}
                          data-testid="kpi-filter-jabatan"
                          className="h-9 px-2 rounded-lg border border-slate-200 text-xs bg-white">
                          <option value="">Semua Jabatan</option>
                          {jabatanOptions.map(j => <option key={j} value={j}>{j}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <div className="flex gap-2">
                        <button type="button" onClick={selectAllVisibleEmp} data-testid="kpi-emp-pilih-semua"
                          className="px-2 py-1 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 font-medium">
                          Pilih semua yang tampil ({modalFilteredEmps.length})
                        </button>
                        <button type="button" onClick={clearEmpSelection} data-testid="kpi-emp-kosongkan"
                          className="px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium">
                          Kosongkan
                        </button>
                      </div>
                      <span className="font-semibold text-violet-700" data-testid="kpi-emp-selected-count">
                        {form.emp_ids.length} karyawan dipilih
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
                      {modalFilteredEmps.length === 0 ? (
                        <p className="p-3 text-xs text-slate-400 italic text-center">Tidak ada karyawan yang cocok</p>
                      ) : modalFilteredEmps.map(e => (
                        <label key={e.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-xs">
                          <input type="checkbox" checked={form.emp_ids.includes(e.id)}
                            onChange={() => toggleEmpSelect(e.id)}
                            data-testid={`kpi-emp-${e.id}`}
                            className="w-3.5 h-3.5 accent-violet-600" />
                          <span className="flex-1 text-slate-800">{e.nama}</span>
                          <span className="text-slate-400 truncate max-w-[45%]">{[e.divisi, e.grade_jabatan, e.branch].filter(Boolean).join(" · ") || "-"}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Template picker */}
              {!editingId && form.emp_ids.length > 0 && (
                <div className={`rounded-lg p-3 border ${suggestedTemplates.length > 0 ? "bg-violet-50 border-violet-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Wand2 className="w-4 h-4 text-violet-600" />
                    <span className="text-xs font-semibold text-slate-700">Load Template Jabatan:</span>
                    <select onChange={e => e.target.value && applyTemplate(e.target.value, true)}
                      data-testid="kpi-load-template"
                      className="h-8 px-2 rounded border border-slate-300 text-xs bg-white flex-1 min-w-[220px]">
                      <option value="">— pilih template —</option>
                      {suggestedTemplates.length > 0 && (
                        <optgroup label={`Sesuai Jabatan (${suggestedTemplates.length})`}>
                          {suggestedTemplates.map(t => (
                            <option key={t.id} value={t.id}>★ {t.nama_template} ({t.items?.length || 0} KPI)</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Semua Template">
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>{t.nama_template} · {t.grade_jabatan || "-"} ({t.items?.length || 0} KPI)</option>
                        ))}
                      </optgroup>
                    </select>
                    {templates.length === 0 && canCreate && (
                      <button onClick={openTplNew}
                        className="text-xs text-violet-700 underline hover:no-underline">
                        Buat template pertama →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* KPI rows */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-700">Daftar KPI ({form.kpis.length})</p>
                  {!editingId && (
                    <button onClick={addKpiRow} data-testid="btn-add-kpi-row"
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                      <Plus className="w-3 h-3" /> Tambah Baris
                    </button>
                  )}
                </div>
                <div className="divide-y divide-slate-100">
                  {form.kpis.map((k, idx) => (
                    <div key={idx} className="p-3 hover:bg-slate-50" data-testid={`kpi-row-form-${idx}`}>
                      <div className="flex items-start gap-2">
                        <span className="flex-shrink-0 mt-2 w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold grid place-items-center">
                          {idx + 1}
                        </span>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                          <label className="md:col-span-5 block">
                            <span className="text-[10px] font-medium text-slate-500">Nama KPI *</span>
                            <input type="text" value={k.nama_kpi}
                              onChange={e => updKpiRow(idx, { nama_kpi: e.target.value })}
                              data-testid={`kpi-nama-${idx}`}
                              placeholder="contoh: Follow-up Chat WA"
                              className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs" />
                          </label>
                          <label className="md:col-span-2 block">
                            <span className="text-[10px] font-medium text-slate-500">Target</span>
                            <input type="number" value={k.target_value}
                              onChange={e => updKpiRow(idx, { target_value: parseFloat(e.target.value) || 0 })}
                              data-testid={`kpi-target-${idx}`}
                              className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                          </label>
                          <label className="md:col-span-2 block">
                            <span className="text-[10px] font-medium text-slate-500">Satuan</span>
                            <select value={k.unit}
                              onChange={e => updKpiRow(idx, { unit: e.target.value })}
                              data-testid={`kpi-unit-${idx}`}
                              className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                              {units.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </label>
                          <label className="md:col-span-2 block">
                            <span className="text-[10px] font-medium text-slate-500">
                              {k.sumber === "csat" ? "Realisasi (otomatis)" : "Realisasi"}
                            </span>
                            <input type="number" value={k.realisasi_value}
                              onChange={e => updKpiRow(idx, { realisasi_value: parseFloat(e.target.value) || 0 })}
                              data-testid={`kpi-realisasi-${idx}`}
                              disabled={k.sumber === "csat"}
                              title={k.sumber === "csat" ? "Realisasi ditarik otomatis dari CSAT — tidak bisa diisi manual" : undefined}
                              className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono disabled:bg-slate-50 disabled:text-slate-400" />
                          </label>
                          <div className="md:col-span-1 flex items-end justify-end">
                            {form.kpis.length > 1 && !editingId && (
                              <button onClick={() => removeKpiRow(idx)}
                                data-testid={`btn-remove-kpi-${idx}`}
                                title="Hapus baris"
                                className="h-9 w-9 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-grid place-items-center">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <label className="md:col-span-4 block">
                            <span className="text-[10px] font-medium text-slate-500">Sumber Realisasi</span>
                            <select value={k.sumber || "manual"}
                              onChange={e => updKpiRow(idx, {
                                sumber: e.target.value,
                                ...(e.target.value === "csat" ? { realisasi_value: 0 } : {}),
                              })}
                              data-testid={`kpi-sumber-${idx}`}
                              className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                              <option value="manual">Diisi manual</option>
                              <option value="csat">Otomatis dari CSAT</option>
                            </select>
                          </label>
                          {k.sumber === "csat" && (
                            <div className="md:col-span-8 flex items-center">
                              <p className="text-[10px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1.5 leading-snug">
                                Angka realisasi ditarik otomatis dari penilaian pelanggan (CSAT). Cukup isi target — realisasi tidak perlu diisi manual.
                              </p>
                            </div>
                          )}
                          <label className="md:col-span-12 block">
                            <span className="text-[10px] font-medium text-slate-500">Catatan (opsional)</span>
                            <input type="text" value={k.catatan || ""}
                              onChange={e => updKpiRow(idx, { catatan: e.target.value })}
                              data-testid={`kpi-catatan-${idx}`}
                              className="mt-0.5 w-full h-8 px-2 rounded border border-slate-200 text-xs" />
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hasil simpan bulk — jujurkan dilewati/gagal, jangan sembunyikan di balik toast sukses */}
              {saveResult && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="kpi-bulk-result">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="font-semibold text-slate-800">
                      {saveResult.dibuat ?? saveResult.created ?? 0} dibuat
                      {(saveResult.dilewati ?? 0) > 0 && ` · ${saveResult.dilewati} dilewati (sudah ada)`}
                      {(saveResult.gagal ?? 0) > 0 && ` · ${saveResult.gagal} gagal`}
                    </p>
                    <button onClick={() => setSaveResult(null)} className="p-1 hover:bg-white rounded"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  {(saveResult.rincian || []).filter(r => r.status !== "dibuat").length > 0 && (
                    <div className="max-h-40 overflow-y-auto border-t border-amber-200 pt-2 space-y-1">
                      {saveResult.rincian.filter(r => r.status !== "dibuat").map((r, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="text-slate-700 truncate">{r.employee_nama || r.emp_id} — {r.nama_kpi}</span>
                          <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                            r.status === "dilewati" ? "bg-sky-100 text-sky-800" : "bg-rose-100 text-rose-800"
                          }`}>{r.status === "dilewati" ? "Dilewati" : "Gagal"}{r.alasan ? `: ${r.alasan}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={kpiModalGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-kpi"
                  className="h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                  <Save className="w-4 h-4" />
                  {editingId ? "Perbarui" : `Simpan ${validKpiCount} KPI × ${form.emp_ids.length} karyawan`}
                </button>
              </div>
              {kpiModalGuard.confirming && (
                <UnsavedGuardBar onStay={kpiModalGuard.cancelClose} onDiscard={kpiModalGuard.confirmDiscard} testid="kpi-modal-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Management Modal */}
      {tplModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={kpiTplGuard.requestClose}>
          <div ref={kpiTplGuard.contentRef} className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="kpi-tpl-modal">
            <div className="bg-gradient-to-r from-violet-700 to-fuchsia-600 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">
                    {tplModal.mode === "list" ? "Template KPI per Jabatan" : (tplModal.tpl?.id ? "Edit Template" : "Buat Template Baru")}
                  </h3>
                  <p className="text-[11px] text-violet-100">Standardisasi KPI untuk 1 posisi/jabatan → pakai berulang di banyak karyawan.</p>
                </div>
              </div>
              <button onClick={kpiTplGuard.requestClose} className="p-1.5 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            {tplModal.mode === "list" ? (
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">Total: <b>{templates.length}</b> template</p>
                  {canCreate && (
                    <button onClick={openTplNew} data-testid="btn-new-tpl"
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium">
                      <Plus className="w-4 h-4" /> Template Baru
                    </button>
                  )}
                </div>
                {templates.length === 0 ? (
                  <div className="p-10 text-center text-sm text-slate-500 bg-slate-50 rounded-lg">
                    <LayoutTemplate className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    Belum ada template. Buat 1 template &rarr; auto-apply ke banyak karyawan dengan jabatan sama.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {templates.map(t => (
                      <div key={t.id} data-testid={`tpl-item-${t.id}`}
                        className="border border-slate-200 rounded-lg p-3 hover:border-violet-400 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm text-slate-900 truncate">{t.nama_template}</p>
                            <p className="text-[11px] text-violet-700 font-medium">Jabatan: {t.grade_jabatan || "— semua —"}</p>
                            {t.deskripsi && <p className="text-[11px] text-slate-500 italic mt-1 line-clamp-2">{t.deskripsi}</p>}
                          </div>
                          <div className="flex gap-1">
                            {canUpdate && (
                              <button onClick={() => openTplEdit(t)} data-testid={`btn-edit-tpl-${t.id}`}
                                className="p-1.5 rounded hover:bg-violet-50 text-slate-400 hover:text-violet-700">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canDelete && (
                              <button onClick={() => delTpl(t.id)} data-testid={`btn-del-tpl-${t.id}`}
                                className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-0.5 mt-2 border-t border-slate-100 pt-2">
                          {(t.items || []).slice(0, 4).map((it, i) => (
                            <p key={i} className="text-[11px] text-slate-600 truncate">
                              · <b>{it.nama_kpi}</b> — target {it.target_value} {it.unit}
                            </p>
                          ))}
                          {(t.items || []).length > 4 && <p className="text-[10px] italic text-slate-400">+{t.items.length - 4} KPI lagi</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-5 max-h-[75vh] overflow-y-auto space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-slate-600">Nama Template *</span>
                    <input value={tplModal.tpl.nama_template} data-testid="tpl-nama"
                      onChange={e => setTplModal(m => ({ ...m, tpl: { ...m.tpl, nama_template: e.target.value } }))}
                      placeholder="mis. KPI Standar Admin Toko"
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Jabatan Target</span>
                    <input value={tplModal.tpl.grade_jabatan} data-testid="tpl-jabatan"
                      onChange={e => setTplModal(m => ({ ...m, tpl: { ...m.tpl, grade_jabatan: e.target.value } }))}
                      list="jabatan-datalist"
                      placeholder="mis. Admin, Mekanik, Gudang"
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                    <datalist id="jabatan-datalist">
                      {[...new Set(emps.map(e => e.grade_jabatan).filter(Boolean))].map(j => <option key={j} value={j} />)}
                    </datalist>
                  </label>
                  <label className="block md:col-span-3">
                    <span className="text-xs font-medium text-slate-600">Deskripsi (opsional)</span>
                    <input value={tplModal.tpl.deskripsi || ""} data-testid="tpl-desc"
                      onChange={e => setTplModal(m => ({ ...m, tpl: { ...m.tpl, deskripsi: e.target.value } }))}
                      placeholder="Deskripsi singkat kapan template ini dipakai"
                      className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  </label>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-700">Item KPI Template ({tplModal.tpl.items?.length || 0})</p>
                    <button onClick={() => setTplModal(m => ({ ...m, tpl: { ...m.tpl, items: [...(m.tpl.items || []), { nama_kpi: "", target_value: 0, unit: "unit", catatan: "" }] } }))}
                      data-testid="btn-add-tpl-item"
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                      <Plus className="w-3 h-3" /> Tambah Item
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(tplModal.tpl.items || []).map((it, idx) => (
                      <div key={idx} className="p-3 hover:bg-slate-50 grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid={`tpl-item-row-${idx}`}>
                        <label className="md:col-span-5 block">
                          <span className="text-[10px] font-medium text-slate-500">Nama KPI</span>
                          <input value={it.nama_kpi}
                            onChange={e => setTplModal(m => { const items = [...m.tpl.items]; items[idx] = { ...items[idx], nama_kpi: e.target.value }; return { ...m, tpl: { ...m.tpl, items } }; })}
                            data-testid={`tpl-item-nama-${idx}`}
                            placeholder="contoh: Kunjungan Client"
                            className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs" />
                        </label>
                        <label className="md:col-span-3 block">
                          <span className="text-[10px] font-medium text-slate-500">Target</span>
                          <input type="number" value={it.target_value}
                            onChange={e => setTplModal(m => { const items = [...m.tpl.items]; items[idx] = { ...items[idx], target_value: parseFloat(e.target.value) || 0 }; return { ...m, tpl: { ...m.tpl, items } }; })}
                            data-testid={`tpl-item-target-${idx}`}
                            className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                        </label>
                        <label className="md:col-span-3 block">
                          <span className="text-[10px] font-medium text-slate-500">Satuan</span>
                          <select value={it.unit}
                            onChange={e => setTplModal(m => { const items = [...m.tpl.items]; items[idx] = { ...items[idx], unit: e.target.value }; return { ...m, tpl: { ...m.tpl, items } }; })}
                            data-testid={`tpl-item-unit-${idx}`}
                            className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                            {units.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </label>
                        <div className="md:col-span-1 flex justify-end">
                          <button onClick={() => setTplModal(m => ({ ...m, tpl: { ...m.tpl, items: m.tpl.items.filter((_, i) => i !== idx) } }))}
                            data-testid={`btn-del-tpl-item-${idx}`}
                            className="h-9 w-9 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-grid place-items-center">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {(tplModal.tpl.items || []).length === 0 && (
                      <div className="p-6 text-center text-xs text-slate-400 italic">Belum ada item. Klik &ldquo;Tambah Item&rdquo; untuk mulai.</div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between gap-2 pt-3 border-t border-slate-100">
                  <button onClick={() => setTplModal({ mode: "list" })}
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm">← Kembali ke Daftar</button>
                  {(tplModal.tpl.id ? canUpdate : canCreate) && (
                    <button onClick={saveTpl} data-testid="btn-save-tpl"
                      className="h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                      <Save className="w-4 h-4" /> {tplModal.tpl.id ? "Perbarui Template" : "Simpan Template"}
                    </button>
                  )}
                </div>
              </div>
            )}
            {kpiTplGuard.confirming && (
              <UnsavedGuardBar onStay={kpiTplGuard.cancelClose} onDiscard={kpiTplGuard.confirmDiscard} testid="kpi-tpl-guard-bar" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SumCard({ label, value, tint, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-3`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}
